import { useEffect, useState, useCallback } from "react";
import api, { getApiErrorMessage } from "@/lib/api";
import { Palette, Image as ImageIcon, Clock, MapPin, Phone, Mail, FileText, Sparkles } from "lucide-react";

const COLORWAY_STYLES = {
  "ocean-slate": { primary: "#22D3C0", accent: "#0F3B44", bg: "#0E1315" },
  "forest-cream": { primary: "#2D6A4F", accent: "#95D5B2", bg: "#F7F5EF" },
  "amber-charcoal": { primary: "#F4B860", accent: "#B7791F", bg: "#1A1614" },
  monochrome: { primary: "#111111", accent: "#666666", bg: "#FFFFFF" },
};

const COLORWAY_LABELS = {
  "ocean-slate": "Ocean Slate",
  "forest-cream": "Forest Cream",
  "amber-charcoal": "Amber Charcoal",
  monochrome: "Monochrome",
};

export default function IntakePanel({ placeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/intake/${encodeURIComponent(placeId)}`);
      setData(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [placeId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="p-6 space-y-3" data-testid="intake-loading">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 border border-line rounded-sm animate-pulse bg-bg-hover" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="intake-error">
        <div className="text-sm text-destructive">{error}</div>
      </div>
    );
  }

  const intake = data?.intake;

  if (!data?.customized || !intake) {
    return (
      <div className="p-6" data-testid="intake-empty">
        <div className="border border-line rounded-sm bg-bg-surface p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1">
            Waiting for customer input
          </div>
          <div className="text-sm text-ink">
            The customer hasn&rsquo;t submitted the &ldquo;Make it yours&rdquo; form yet.
            When they fill it in on the sample page, their picks (colors, logo, slogan,
            services, hours, service area, photos, contact) will appear here for you to
            build against.
          </div>
          <div className="mt-3 text-[11px] font-mono text-ink-muted">
            Widget endpoint: <span className="text-cyan">POST /api/intake/{placeId}</span>
          </div>
        </div>
      </div>
    );
  }

  const style = COLORWAY_STYLES[intake.colorway];

  return (
    <div className="p-6 space-y-4" data-testid="intake-panel">
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-medium text-ink flex items-center gap-2">
          <Sparkles size={14} className="text-cyan" />
          &ldquo;Make it yours&rdquo; intake
        </h3>
        <div className="text-[11px] text-ink-muted font-mono">
          submitted {formatTs(intake.submitted_at)}
        </div>
      </div>

      {intake.colorway && (
        <IntakeCard icon={Palette} label="Colorway">
          <div className="flex items-center gap-3" data-testid={`intake-colorway-${intake.colorway}`}>
            <div className="flex border border-line rounded-sm overflow-hidden h-8 w-24">
              <div className="flex-1" style={{ backgroundColor: style?.primary }} />
              <div className="flex-1" style={{ backgroundColor: style?.accent }} />
              <div className="flex-1" style={{ backgroundColor: style?.bg }} />
            </div>
            <div>
              <div className="text-sm text-ink">{COLORWAY_LABELS[intake.colorway] || intake.colorway}</div>
              <div className="text-[11px] text-ink-muted font-mono">{intake.colorway}</div>
            </div>
          </div>
        </IntakeCard>
      )}

      {intake.logo && (
        <IntakeCard icon={ImageIcon} label="Logo">
          <div
            className="h-16 w-32 border border-line rounded-sm bg-bg flex items-center justify-center overflow-hidden"
            data-testid="intake-logo"
          >
            <img src={intake.logo} alt="Client logo" className="max-h-full max-w-full object-contain" />
          </div>
        </IntakeCard>
      )}

      {intake.slogan && (
        <IntakeCard icon={FileText} label="Slogan">
          <div className="text-sm text-ink italic" data-testid="intake-slogan">
            &ldquo;{intake.slogan}&rdquo;
          </div>
        </IntakeCard>
      )}

      {intake.services && intake.services.length > 0 && (
        <IntakeCard icon={FileText} label="Services">
          <div className="flex flex-wrap gap-1.5" data-testid="intake-services">
            {intake.services.map((s, i) => (
              <span
                key={i}
                className="text-xs border border-line rounded-sm px-2 py-0.5 text-ink bg-bg"
              >
                {s}
              </span>
            ))}
          </div>
        </IntakeCard>
      )}

      {intake.hours && (
        <IntakeCard icon={Clock} label="Hours">
          <div className="text-sm text-ink whitespace-pre-wrap" data-testid="intake-hours">{intake.hours}</div>
        </IntakeCard>
      )}

      {intake.service_area && (
        <IntakeCard icon={MapPin} label="Service area">
          <div className="text-sm text-ink" data-testid="intake-service-area">{intake.service_area}</div>
        </IntakeCard>
      )}

      {intake.contact && (Object.keys(intake.contact).length > 0) && (
        <IntakeCard icon={Phone} label="Contact">
          <div className="text-sm text-ink space-y-1" data-testid="intake-contact">
            {intake.contact.phone && (
              <div className="flex items-center gap-2">
                <Phone size={11} className="text-cyan" />
                <a href={`tel:${intake.contact.phone}`} className="text-cyan font-mono">
                  {intake.contact.phone}
                </a>
              </div>
            )}
            {intake.contact.email && (
              <div className="flex items-center gap-2">
                <Mail size={11} className="text-cyan" />
                <a href={`mailto:${intake.contact.email}`} className="text-cyan font-mono">
                  {intake.contact.email}
                </a>
              </div>
            )}
            {intake.contact.address && (
              <div className="flex items-center gap-2">
                <MapPin size={11} className="text-cyan" />
                <span>{intake.contact.address}</span>
              </div>
            )}
          </div>
        </IntakeCard>
      )}

      {intake.photos && intake.photos.length > 0 && (
        <IntakeCard icon={ImageIcon} label={`Photos (${intake.photos.length})`}>
          <div className="grid grid-cols-4 gap-2" data-testid="intake-photos">
            {intake.photos.map((p, i) => (
              <div
                key={i}
                className="aspect-square border border-line rounded-sm bg-bg overflow-hidden"
              >
                <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </IntakeCard>
      )}

      {intake.notes && (
        <IntakeCard icon={FileText} label="Notes from customer">
          <div className="text-sm text-ink whitespace-pre-wrap" data-testid="intake-notes">
            {intake.notes}
          </div>
        </IntakeCard>
      )}
    </div>
  );
}

function IntakeCard({ icon: Icon, label, children }) {
  return (
    <div className="border border-line rounded-sm bg-bg-surface">
      <div className="px-4 py-2 border-b border-line flex items-center gap-2">
        <Icon size={12} className="text-ink-muted" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          {label}
        </div>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
